import { useState } from 'react';
import { Page, PageHeader, SearchSelect } from '@evogard/ui';
import {
  EXPENSE_REQUEST_RESOURCES,
  EXPENSE_REQUEST_STAGES,
  EXPENSE_REQUEST_STAGE_LABELS,
  EXPENSE_SUPERVISE_ACTION,
  type ExpenseRequestStage,
} from '@evogard/domain';
import { SupervisorExpenseBoard } from '../../../components/expense/SupervisorExpenseBoard.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { useClaimExpenseMutations, useSupervisorBoard } from './useClaimExpenses.js';

/** Os departamentos em que esta pessoa supervisiona a despesa, na ordem da esteira. */
export function supervisedStages(
  context: Parameters<typeof can>[0],
): ExpenseRequestStage[] {
  return EXPENSE_REQUEST_STAGES.filter((s) =>
    can(context, EXPENSE_REQUEST_RESOURCES[s], EXPENSE_SUPERVISE_ACTION),
  );
}

/**
 * Sinistros › Despesas (26/10/2026): o quadro do supervisor da despesa.
 *
 * **Um item de menu só para os cinco departamentos** (pedido do dono): a tela
 * mostra os departamentos que a pessoa supervisiona. Quem supervisiona um só
 * cai direto no quadro dele, com o departamento fixo; quem supervisiona mais
 * de um escolhe no alto. Cada departamento continua cobrando o próprio recurso
 * na API — o supervisor do Reparo não abre o da Análise nem pelo endereço.
 */
export function SupervisorBoardScreen() {
  const { data: context } = useSessionContext();
  const stages = supervisedStages(context);
  const [chosen, setChosen] = useState<ExpenseRequestStage | null>(null);
  const stage = chosen !== null && stages.includes(chosen) ? chosen : (stages[0] ?? null);

  return (
    <Page measure="cheia">
      <PageHeader title="Despesas">
        As despesas lançadas nos cards do seu departamento. Aprove, pendencie ou reprove o
        que está em Solicitação; depois que a despesa chega ao Financeiro, só ele decide,
        e você volta a agir se ele abrir uma pendência.
      </PageHeader>
      {stage !== null && stages.length > 1 && (
        <div className="max-w-xs">
          <SearchSelect
            label="Departamento"
            options={stages.map((s) => ({
              value: s,
              label: EXPENSE_REQUEST_STAGE_LABELS[s],
            }))}
            value={stage}
            onChange={(v) => setChosen((v || null) as ExpenseRequestStage | null)}
          />
        </div>
      )}
      {stage !== null && <Board stage={stage} fixed={stages.length === 1} />}
    </Page>
  );
}

function Board({ stage, fixed }: { stage: ExpenseRequestStage; fixed: boolean }) {
  const board = useSupervisorBoard(stage);
  const { act } = useClaimExpenseMutations();
  const name = EXPENSE_REQUEST_STAGE_LABELS[stage];
  return (
    <SupervisorExpenseBoard
      label={`Quadro de despesas de ${name}`}
      department={fixed ? name : undefined}
      query={board}
      onAct={(id, a, reason) => act.mutateAsync({ id, act: a, reason })}
    />
  );
}
